// ---------------------------------------------------------------------------
// The sign-in, on the design system, over REST (R13)
//
// The session guard (auth/session-guard.ts) shows this instead of a page when
// nobody is signed in, at the address the person asked for; once they are,
// the guard shows that page there. It replaces Adornis's functionalLogin
// (@adornis/user-forms), which signed in over the BaseQL socket.
//
//   - "Username or email" and "Password" in one form with a submit button, so
//     Enter signs in; autofilled by a password manager (autocomplete username
//     and current-password, named username and password);
//   - an empty field is named at the field and nothing is sent;
//   - the server's refusal in its own words, in a critical Callout (announced):
//     the same for an unknown name and a wrong password, so the page tells no
//     more than the server; the wait after too many attempts; a server that
//     cannot sign anybody in right now; no connection;
//   - the fields keep what was typed; the busy button keeps its label and
//     takes no second click; a browser drops the focus of a button that turns
//     disabled (Chrome gives it back to the button once it is enabled again),
//     so after a refusal the first field to fix takes it, unless it is in a
//     field, where Enter left it;
//   - nothing from the address: functionalLogin showed ?error_message= as
//     the error, and a crafted link could print any text here; nothing in
//     the app sets it, so the page reads it no more (Ferenc 30.10., R13b);
//   - a session that ended while the app was open says so, and the page takes
//     focus, since the page the person was on has gone;
//   - a server that could not be asked at the start says so, with Try again.
// No TOTP: the server only ever registered the password flow (decision 7.16).
// The tab title is session-guard.ts's (startSignInTitle).
//
// A LitElement with light DOM; the guard hands it the session.
// ---------------------------------------------------------------------------

import { html, LitElement, type PropertyValues } from 'lit';

import type { Session } from '../auth/session.js';
import { button } from '../design/button.js';
import { callout } from '../design/callout.js';
import { textField } from '../design/field.js';
import { standalonePage } from './standalone-page.js';

const NAME_MISSING = 'Enter your username or email.';
const PASSWORD_MISSING = 'Enter your password.';

export class SdrSignInPage extends LitElement {
  static override properties = {
    session: { attribute: false },
    ended: { type: Boolean },
    unreachable: { type: Boolean },
    username: { state: true },
    password: { state: true },
    busy: { state: true },
    retrying: { state: true },
    failure: { state: true },
    missing: { state: true },
  };

  declare session: Session | undefined;
  /** The session ended while the app was open. */
  declare ended: boolean;
  /** The server could not be asked at the start. */
  declare unreachable: boolean;
  declare username: string;
  declare password: string;
  declare busy: boolean;
  declare retrying: boolean;
  /** The last refusal, in the server's words. */
  declare failure: string;
  declare missing: { username: boolean; password: boolean };

  constructor() {
    super();
    this.ended = false;
    this.unreachable = false;
    this.username = '';
    this.password = '';
    this.busy = false;
    this.retrying = false;
    this.failure = '';
    this.missing = { username: false, password: false };
  }

  override createRenderRoot() {
    return this;
  }

  protected override firstUpdated(_changed: PropertyValues) {
    // The page the person was on has gone; a screen reader learns why from here.
    if (!this.ended) return;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    this.querySelector<HTMLElement>('h1')?.focus({ preventScroll: true });
  }

  private async submit(e: Event) {
    e.preventDefault();
    if (this.busy || !this.session) return;
    const username = this.username.trim();
    this.missing = { username: !username, password: !this.password };
    if (!username || !this.password) {
      this.failure = '';
      this.focusField(!username ? 'sign-in-username' : 'sign-in-password', true);
      return;
    }
    this.busy = true;
    const result = await this.session.signIn(username, this.password);
    // Signed in: the guard shows the page, and this one goes.
    if (result.ok) return;
    this.busy = false;
    this.failure = result.error;
    this.unreachable = false;
    this.focusField('sign-in-username', false);
  }

  /**
   * Focus a field: always, or only when the focus was dropped: on nothing, or on the busy button,
   * which Chrome hands it back to once the button is enabled again (found in Chrome).
   */
  private focusField(id: string, always: boolean) {
    void this.updateComplete.then(() => {
      const active = document.activeElement;
      const dropped =
        !active ||
        active === document.body ||
        !active.isConnected ||
        active === this.querySelector('button[type="submit"]');
      if (!always && !dropped) return;
      this.querySelector<HTMLInputElement>(`#${id}`)?.focus();
    });
  }

  private async tryAgain() {
    if (!this.session || this.retrying) return;
    this.retrying = true;
    try {
      await this.session.check();
    } finally {
      this.retrying = false;
    }
  }

  /** One Callout per page: the latest refusal first, then why the page shows at all. */
  private notice(): unknown {
    if (this.failure) return callout({ tone: 'critical' }, this.failure);
    if (this.unreachable)
      return callout(
        {
          tone: 'critical',
          title: 'Could not reach SDR Agent',
          actions: button({ size: 'sm', loading: this.retrying, onClick: () => void this.tryAgain() }, 'Try again'),
        },
        'Check your connection, then try again.',
      );
    if (this.ended) return callout({ tone: 'info' }, 'Your session has ended. Sign in again to continue.');
    return null;
  }

  override render() {
    const notice = this.notice();
    return standalonePage(
      { title: 'Sign in to SDR Agent' },
      html`${notice}
        <form novalidate @submit=${(e: Event) => void this.submit(e)}>
          ${textField({
            id: 'sign-in-username',
            label: 'Username or email',
            name: 'username',
            autocomplete: 'username',
            value: this.username,
            error: this.missing.username ? NAME_MISSING : undefined,
            onInput: (e: InputEvent) => {
              this.username = (e.target as HTMLInputElement).value;
              if (this.missing.username) this.missing = { ...this.missing, username: !this.username.trim() };
            },
          })}
          ${textField({
            id: 'sign-in-password',
            label: 'Password',
            type: 'password',
            name: 'password',
            autocomplete: 'current-password',
            value: this.password,
            error: this.missing.password ? PASSWORD_MISSING : undefined,
            onInput: (e: InputEvent) => {
              this.password = (e.target as HTMLInputElement).value;
              if (this.missing.password) this.missing = { ...this.missing, password: !this.password };
            },
          })}
          ${button({ type: 'submit', variant: 'primary', block: true, loading: this.busy }, 'Sign in')}
        </form>`,
    );
  }
}

if (!customElements.get('sdr-sign-in-page')) customElements.define('sdr-sign-in-page', SdrSignInPage);
